/**
 * Early-vote / absentee state on a voter, as the backend exposes it:
 * has_voted (ballot_status starts with ACCEPTED), ballot_method
 * ('early' | 'mail'), ballot_returned_on ('YYYY-MM-DD') and the raw NCSBE
 * ballot_status. ballot_status null means "not in the latest file"; '' means
 * a ballot was requested but not returned.
 */

export interface BallotFields {
  has_voted?: boolean;
  ballot_method?: 'early' | 'mail' | null;
  ballot_returned_on?: string | null;
  ballot_status?: string | null;
}

export interface BallotBadge {
  label: string;
  kind: 'voted' | 'pending';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD' -> 'Oct 16'. Parsed by hand: new Date('2026-10-16') is UTC
 * midnight and prints Oct 15 in any zone west of UTC. */
export function formatBallotDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return '';
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}` : '';
}

/** The voter-detail badge: null when there is nothing worth saying. */
export function ballotBadge(v: BallotFields): BallotBadge | null {
  const date = formatBallotDate(v.ballot_returned_on);
  const suffix = date ? ` · ${date}` : '';
  if (v.has_voted) {
    if (v.ballot_method === 'early') return { label: `Voted early${suffix}`, kind: 'voted' };
    if (v.ballot_method === 'mail') return { label: `Mail ballot accepted${suffix}`, kind: 'voted' };
    return { label: `Voted${suffix}`, kind: 'voted' };
  }
  const mail = v.ballot_method === 'mail';
  if (v.ballot_status === 'PENDING CURE') {
    return { label: mail ? 'Mail ballot pending cure' : 'Ballot pending cure', kind: 'pending' };
  }
  if (v.ballot_status === '' && mail) {
    return { label: 'Mail ballot requested, not returned', kind: 'pending' };
  }
  return null;
}
